<script lang="ts" setup>
import { computed } from 'vue';
import ConnxIcon from '../ConnxIcon.vue';
import type AvailableIcons from '@/interfaces/AvailableIcons';
import { buttonStyleCssClassMap, buttonPaddingMap } from '@/lib/button-styles';

type ButtonType = 'button' | 'submit' | 'reset';
type IconPosition = 'before' | 'after';
export type ButtonStyles =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'success-alt'
  | 'danger'
  | 'danger-alt'
  | 'warning'
  | 'warning-alt'
  | 'info'
  | 'info-alt'
  | 'brand-primary'
  | 'white'
  | 'underlined'
  | 'blank'
  | 'secondary-alt'
  | 'icon'
  | 'circular';

interface Props {
  unpadded?: boolean;
  type?: ButtonType;
  link?: string | null;
  disabled?: boolean;
  active?: boolean;
  icon?: AvailableIcons | null;
  iconPosition?: IconPosition | null;
  iconClass?: string | null;
  buttonStyle?: ButtonStyles;
  buttonSize?: 'sm' | 'md' | 'lg';
  class?: string;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  link: null,
  icon: null,
  iconClass: null,
  iconPosition: 'after',
  buttonStyle: 'secondary',
  buttonSize: 'lg',
});

const iconClassList = computed(() => {
  let cssList = props.iconClass || '';
  if (props.buttonStyle === 'secondary') {
    cssList += ' tw-text-connx-grey';
  }
  return cssList;
});

// Return base classes as an array for :class binding
const baseClassList = computed(() => {
  const classes = [];

  // Add button style classes from the map
  const styleClasses = buttonStyleCssClassMap.get(props.buttonStyle) || [];
  classes.push(...styleClasses);

  // Add size class
  const sizeClass = buttonPaddingMap.get(props.buttonSize);
  if (sizeClass && !props.unpadded) {
    classes.push(sizeClass);
  }

  // Add active state for secondary-alt
  if (props.buttonStyle === 'secondary-alt' && props.active) {
    classes.push('Connx-btn--active');
  }

  // Add icon positioning
  if (props.icon && props.iconPosition === 'before') {
    classes.push('Connx-btn--icon-before');
  } else if (props.icon) {
    classes.push('Connx-btn--icon-after');
  } else {
    classes.push('Connx-btn--no-icon');
  }

  // Add disabled state
  if (props.disabled) {
    classes.push('Connx-btn--disabled');
  }

  return classes;
});

defineEmits(['click']);
</script>

<template>
  <a
    v-if="link"
    :disabled="disabled"
    :class="[baseClassList, props.class]"
    :href="link"
    :role="type"
    @click="$emit('click')"
  >
    <connx-icon
      v-if="icon && iconPosition === 'before'"
      :name="icon"
      class="Connx-btn__icon Connx-btn__icon--before"
      :class="iconClassList"
    />
    <slot></slot>
    <connx-icon
      v-if="icon && iconPosition === 'after'"
      :name="icon"
      class="Connx-btn__icon Connx-btn__icon--after"
      :class="iconClassList"
    />
  </a>

  <button
    v-else
    :disabled="disabled"
    :active="active"
    :class="[baseClassList, props.class]"
    :role="type"
    :type="type"
    @click="$emit('click')"
  >
    <connx-icon
      v-if="icon && iconPosition === 'before'"
      :name="icon"
      class="Connx-btn__icon Connx-btn__icon--before"
      :class="iconClassList"
    />
    <slot></slot>
    <connx-icon
      v-if="icon && iconPosition === 'after'"
      :name="icon"
      class="Connx-btn__icon Connx-btn__icon--after"
      :class="iconClassList"
    />
  </button>
</template>
